<template>
  <div>
    <div ref="rowRef" class="mt-3 flex items-center gap-2 overflow-hidden">
      <span class="mr-1 shrink-0 text-xs font-medium text-gray-60">{{
        $t('conversations.filters.ai_search.example_label')
      }}</span>
      <ui-tag
        v-for="(suggestion, index) in candidates"
        :key="suggestion.key"
        data-suggestion-chip
        theme="zennovate"
        tag="button"
        :class="{ hidden: index >= visibleCount }"
        @click="useSuggestion(suggestion.label)"
      >
        {{ suggestion.label }}
      </ui-tag>
      <button
        ref="refreshRef"
        type="button"
        :aria-label="$t('conversations.filters.ai_search.refresh_button')"
        :title="$t('conversations.filters.ai_search.refresh_button')"
        class="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-50 transition-colors hover:bg-gray-10 hover:text-gray-70"
        @click="shuffle"
      >
        <refresh-cw-icon class="icon-s" />
      </button>
    </div>
    <div v-if="isLoading" class="mt-8 flex items-center justify-center gap-2 p-2">
      <ui-spinner inline no-spinner-background spinner-color="blue-40" />
      <span class="text-xs text-gray-70">{{ $t(loadingStepLabelKey) }}</span>
    </div>
    <ui-alert v-else-if="statusAlert" :error="statusAlert.error" :warning="!statusAlert.error" compressed full-width class="mt-8">
      {{ statusAlert.message }}
    </ui-alert>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { UiSpinner, UiTag, UiAlert } from '@klausapp/ui-kit'
import { RefreshCwIcon } from '@klausapp/ui-kit/icons'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'

import { deconstructFilterOptions, type DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import { getAiFilterOptions, AiFilterStatus } from '../../api/ai-filters'
import { usePromptSuggestions } from './usePromptSuggestions'
import { useProgressiveLoading } from './useProgressiveLoading'

const props = defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  intelligentFields: DefaultFilterOption[]
  prompt: string
}>()

const emit = defineEmits<{
  (e: 'ai-results', value: DeconstructedOption[]): void
  (e: 'use-suggestion', value: string): void
  (e: 'loading', value: boolean): void
}>()

const assistantResponse = ref('')
const submittedPrompt = ref('')
const hasError = ref(false)
const status = ref<AiFilterStatus | undefined>(undefined)

const statusAlert = computed(() => {
  if (hasError.value) return { error: true, message: i18n.t('conversations.filters.ai_search.error_message') }
  if (status.value === AiFilterStatus.UNMET)
    return { error: false, message: assistantResponse.value || i18n.t('conversations.filters.ai_search.no_match') }
  if (status.value === AiFilterStatus.PARTIAL)
    return {
      error: false,
      message: assistantResponse.value || i18n.t('conversations.filters.ai_search.partial_match'),
    }
  return null
})

const rowRef = useTemplateRef<HTMLElement>('rowRef')
const refreshRef = useTemplateRef<HTMLElement>('refreshRef')
const { candidates, visibleCount, shuffle } = usePromptSuggestions(rowRef, refreshRef)

const queryClient = useQueryClient()
const queryKey = ['ai-filter-options', session.workspace.id, submittedPrompt]

const { refetch, isFetching: isLoading } = useQuery({
  queryKey,
  queryFn: ({ signal }) => getAiFilterOptions(submittedPrompt.value, signal),
  enabled: false,
  refetchOnMount: false,
})

const { loadingStepLabelKey, start: startProgressiveLoading, stop: clearLoadingTimers } = useProgressiveLoading()

const useSuggestion = (suggestion: string) => {
  emit('use-suggestion', suggestion)
  submitWithPrompt(suggestion)
}

const submit = () => submitWithPrompt(props.prompt)

let latestRequestId = 0
let isMounted = true

onBeforeUnmount(() => {
  isMounted = false
})

async function submitWithPrompt(prompt: string) {
  if (!prompt || (isLoading.value && prompt === submittedPrompt.value)) return

  const requestId = ++latestRequestId

  await queryClient.cancelQueries({ queryKey })

  // Another submit started during the await above — let it own the state.
  if (requestId !== latestRequestId) return

  submittedPrompt.value = prompt
  hasError.value = false
  status.value = undefined
  startProgressiveLoading()
  emit('loading', true)

  const { data: response, error } = await refetch()

  if (requestId !== latestRequestId) return

  // Modal closed mid-flight: the query was aborted on unmount, so drop the (expected) rejection
  // rather than surfacing it as an error.
  if (!isMounted) return

  clearLoadingTimers()

  if (error || !response) {
    console.error('Error fetching AI filter options', error)
    hasError.value = true
    emit('loading', false)
    return
  }

  status.value = response.status
  assistantResponse.value = response.assistantResponse || ''

  // Keep the date-only view so the user can rephrase — don't apply the empty results.
  if (response.status === AiFilterStatus.UNMET) {
    emit('loading', false)
    return
  }

  // The endpoint returns AutoQA/Intelligent options inside `options`, so re-route them by name into
  // the correct buckets — otherwise they'd be sent as invalid default options and rejected.
  const autoQaNames = new Set((props.autoqaFields || []).map((f) => f.filterOption))
  const intelligentNames = new Set((props.intelligentFields || []).map((f) => f.filterOption))

  const returnedOptions = response.options || []
  const defaultOptions = returnedOptions.filter((o) => !autoQaNames.has(o.option) && !intelligentNames.has(o.option))
  const autoQaOptions = returnedOptions.filter((o) => autoQaNames.has(o.option))
  const intelligentOptions = returnedOptions.filter((o) => intelligentNames.has(o.option))

  const results = deconstructFilterOptions({
    options: defaultOptions,
    autoQaOptions,
    spotlightOptions: [],
    customCategoryOptions: [],
    intelligentFilterOptions: intelligentOptions,
    defaultOptions: props.options || [],
    defaultAutoQaOptions: props.autoqaFields || [],
    defaultSpotlightOptions: props.spotlightFields || [],
    defaultIntelligentFilterOptions: props.intelligentFields || [],
  })

  emit('ai-results', results)
  emit('loading', false)
}

defineExpose({ submit })
</script>
